import React, { useState, useEffect } from 'react';
import { tasksApi } from '../../api/tasks';
import { ChevronLeft, ChevronRight, Clock } from 'lucide-react';
import WidgetCard from './WidgetCard';
import WidgetModal from './WidgetModal';

export default function MiniCalendar({ recordId }) {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);

  useEffect(() => {
    tasksApi.getForRecord(recordId)
      .then(res => { const data = res.data || res; const all = []; Object.values(data.grouped || {}).forEach(arr => all.push(...arr)); setTasks(all.filter(t => t.due_date)); })
      .catch(() => {}).finally(() => setLoading(false));
  }, [recordId]);

  const year = currentDate.getFullYear(), month = currentDate.getMonth(), today = new Date();
  const firstDay = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const monthName = currentDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });

  const getTasksForDay = (day) => {
    const ds = `${year}-${String(month+1).padStart(2,'0')}-${String(day).padStart(2,'0')}`;
    return tasks.filter(t => t.due_date?.startsWith(ds));
  };

  const days = [];
  for (let i = 0; i < firstDay; i++) days.push(null);
  for (let d = 1; d <= daysInMonth; d++) days.push(d);

  const isToday = (d) => d === today.getDate() && month === today.getMonth() && year === today.getFullYear();
  const isPast = (d) => new Date(year, month, d) < new Date(today.getFullYear(), today.getMonth(), today.getDate());

  const upcoming = tasks
    .filter(t => t.status !== 'completed' && t.status !== 'cancelled' && new Date(t.due_date) >= new Date(today.getFullYear(), today.getMonth(), today.getDate()))
    .sort((a, b) => new Date(a.due_date) - new Date(b.due_date));

  const CalendarGrid = () => (
    <div>
      <div className="flex items-center justify-between mb-2">
        <button onClick={() => setCurrentDate(new Date(year, month-1, 1))} className="p-1 rounded hover:bg-gray-100"><ChevronLeft className="h-4 w-4 text-gray-500" /></button>
        <span className="text-xs font-semibold text-gray-700">{monthName}</span>
        <button onClick={() => setCurrentDate(new Date(year, month+1, 1))} className="p-1 rounded hover:bg-gray-100"><ChevronRight className="h-4 w-4 text-gray-500" /></button>
      </div>
      <div className="grid grid-cols-7 gap-0.5 mb-1">
        {['Su','Mo','Tu','We','Th','Fr','Sa'].map(d => <div key={d} className="text-center text-[10px] font-medium text-gray-400 py-0.5">{d}</div>)}
      </div>
      <div className="grid grid-cols-7 gap-0.5">
        {days.map((day, i) => {
          if (!day) return <div key={'e'+i} />;
          const dt = getTasksForDay(day), hasOverdue = dt.some(t => isPast(day) && t.status !== 'completed'), has = dt.length > 0;
          return (
            <div key={day} title={dt.map(t=>t.title).join(', ')||undefined}
              className={`relative text-center py-1 text-[11px] rounded-md ${isToday(day)?'bg-brand-600 text-white font-bold':'text-gray-700 hover:bg-gray-100'} ${hasOverdue&&!isToday(day)?'text-red-600 font-semibold':''}`}>
              {day}
              {has && <div className={`absolute bottom-0 left-1/2 -translate-x-1/2 h-1 w-1 rounded-full ${hasOverdue?'bg-red-500':isToday(day)?'bg-white':'bg-brand-500'}`}/>}
            </div>
          );
        })}
      </div>
    </div>
  );

  return (
    <>
      <WidgetCard title="Calendar" icon="📅" onExpand={() => setModalOpen(true)}>
        <div className="p-3"><CalendarGrid /></div>
      </WidgetCard>
      <WidgetModal open={modalOpen} onClose={() => setModalOpen(false)} title="Calendar & Deadlines" icon="📅" width="max-w-xl">
        <div className="p-6">
          <CalendarGrid />
          {upcoming.length > 0 && (
            <div className="mt-4 border-t border-gray-200 pt-4">
              <p className="text-xs font-semibold text-gray-500 uppercase mb-2">Upcoming Deadlines</p>
              <div className="space-y-2">
                {upcoming.map(t => (
                  <div key={t.id} className="flex items-center gap-3 text-sm p-2 rounded-lg hover:bg-gray-50">
                    <Clock className="h-4 w-4 text-gray-400" />
                    <span className="flex-1 truncate text-gray-700">{t.title}</span>
                    <span className="text-xs text-gray-400">{new Date(t.due_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</span>
                    <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${
                      t.priority==='urgent'?'bg-red-100 text-red-700':t.priority==='high'?'bg-orange-100 text-orange-700':'bg-gray-100 text-gray-600'}`}>{t.priority}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </WidgetModal>
    </>
  );
}
